/**
 * 日期工具函数
 *
 * 背景：项目使用 Element Plus 1.0.2-beta.33，
 * el-date-picker 的 value-format 属性在该版本下不可靠，
 * 绑定值可能是 Date 对象而非预期格式字符串，导致字符串拼接、
 * 正则校验等逻辑失效。
 *
 * 所有需要从 date-picker 取值并提交给后端的地方，
 * 都应使用本文件提供的工具函数做规范化处理。
 */

const pad = n => (n < 10 ? '0' + n : '' + n);

/**
 * 将任意日期值（Date 对象 / 字符串 / 时间戳）格式化为本地时区的日期字符串。
 *
 * @param {Date|string|number} value - 待格式化的值
 * @param {string} [fmt='YYYY-MM-DD'] - 格式，支持 YYYY MM DD HH mm ss SSS
 * @returns {string} 格式化后的字符串；值无效时返回空串
 *
 * @example
 *   formatDate(new Date())                    // '2026-09-01'
 *   formatDate(new Date(), 'YYYY-MM-DD HH:mm:ss') // '2026-09-01 06:22:41'
 *   formatDate('2026-09-01', 'YYYY-MM-DD')   // '2026-09-01'（已是字符串直接返回同格式）
 */
export function formatDate(value, fmt = 'YYYY-MM-DD') {
  if (value === null || value === undefined || value === '') return '';

  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return '';

  return fmt
    .replace('YYYY', date.getFullYear())
    .replace('MM', pad(date.getMonth() + 1))
    .replace('DD', pad(date.getDate()))
    .replace('HH', pad(date.getHours()))
    .replace('mm', pad(date.getMinutes()))
    .replace('ss', pad(date.getSeconds()))
    .replace('SSS', String(date.getMilliseconds()).padStart(3, '0'));
}

/**
 * 格式化为本地时区的 ISO 8601 字符串（带时区偏移）。
 * 例：2026-09-01T06:22:41.966+08:00
 *
 * @param {Date|string|number} value
 * @returns {string}
 */
export function toLocalISOString(value) {
  if (value === null || value === undefined || value === '') return '';

  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return '';

  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const offsetHours = pad(Math.floor(Math.abs(offset) / 60));
  const offsetMinutes = pad(Math.abs(offset) % 60);

  return `${formatDate(date, 'YYYY-MM-DDTHH:mm:ss.SSS')}${sign}${offsetHours}:${offsetMinutes}`;
}

/**
 * 将本地日期转换为 UTC 时区的 ISO 8601 字符串（以 Z 结尾）。
 *
 * 语义：用户在日期选择器里选的"9月10号"是本地时间的9月10号，
 * 此函数把它转成对应的 UTC 时间字符串提交给后台。
 * 列表展示时，后台返回 UTC 时间，前端按本地时区格式化，日期就能对上。
 *
 * 例（东八区）：选 2026-09-10
 *     → toUTCDateTime(date, 'start') = '2026-09-09T16:00:00.000Z'  (=本地10号 00:00)
 *     → toUTCDateTime(date, 'end')   = '2026-09-10T15:59:59.000Z'  (=本地10号 23:59)
 *
 * 后台 Go 服务的 time.Time 字段通常按 "2006-01-02T15:04:05.999Z" 格式解析。
 *
 * @param {Date|string|number} value - 日期（本地时区）
 * @param {'start'|'end'} [timeOfDay='start'] - 取当天开始(00:00:00)还是结束(23:59:59)
 * @returns {string}
 */
export function toUTCDateTime(value, timeOfDay = 'start') {
  if (value === null || value === undefined || value === '') return '';

  const dateStr = formatDate(value, 'YYYY-MM-DD');
  if (!dateStr) return '';

  const [y, m, d] = dateStr.split('-').map(Number);
  const hours = timeOfDay === 'end' ? 23 : 0;
  const minutes = timeOfDay === 'end' ? 59 : 0;
  const seconds = timeOfDay === 'end' ? 59 : 0;
  // 按本地时区构造 Date 对象，再转 UTC ISO 字符串
  const localDate = new Date(y, m - 1, d, hours, minutes, seconds, 0);
  return localDate.toISOString();
}

/**
 * 判断值是否为 Date 对象。
 * @param {*} value
 * @returns {boolean}
 */
export function isDate(value) {
  return value instanceof Date && !isNaN(value.getTime());
}
